/**
 * Authentication Recovery Utilities
 *
 * This module provides utilities to detect and recover from authentication
 * redirect loops and other auth-related issues.
 */

import { getEnvConfig } from "~/services/appConfig";

export interface AuthRecoveryOptions {
  clearStorage?: boolean;
  redirectToHome?: boolean;
  maxRetries?: number;
}

/**
 * Check if we're currently in a Microsoft authentication URL
 */
export function isInMicrosoftAuthFlow(): boolean {
  const url = window.location.href;
  return (
    url.includes("login.microsoftonline.com") ||
    url.includes("b2clogin.com") ||
    url.includes("reprocess") ||
    url.includes("oauth/authorize")
  );
}

/**
 * Check if we've been in the auth flow for too long
 */
export function isAuthFlowStuck(): boolean {
  const authFlowStart = sessionStorage.getItem("authFlowStartTime");
  if (!authFlowStart) {
    return false;
  }

  const startTime = parseInt(authFlowStart);
  const currentTime = Date.now();
  const maxAuthFlowDuration = 5 * 60 * 1000; // 5 minutes

  return currentTime - startTime > maxAuthFlowDuration;
}

/**
 * Track when we enter the auth flow
 */
export function trackAuthFlowStart(): void {
  if (isInMicrosoftAuthFlow()) {
    const existing = sessionStorage.getItem("authFlowStartTime");
    if (!existing) {
      sessionStorage.setItem("authFlowStartTime", Date.now().toString());
    }
  }
}

/**
 * Clear auth flow tracking
 */
export function clearAuthFlowTracking(): void {
  sessionStorage.removeItem("authFlowStartTime");
}

/**
 * Perform emergency auth recovery
 */
export function performEmergencyAuthRecovery(
  options: AuthRecoveryOptions = {},
): void {
  const {
    clearStorage = true,
    redirectToHome = true,
    maxRetries = 3,
  } = options;

  // Track recovery attempts
  const recoveryAttempts = parseInt(
    sessionStorage.getItem("authRecoveryAttempts") ?? "0",
  );

  if (recoveryAttempts >= maxRetries) {
    alert(
      "Authentication system requires manual reset. Please clear your browser cache and cookies, then refresh the page.",
    );
    return;
  }

  sessionStorage.setItem(
    "authRecoveryAttempts",
    (recoveryAttempts + 1).toString(),
  );

  if (clearStorage) {
    console.log("🧹 Clearing authentication storage");

    // Clear localStorage auth keys
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (
        key &&
        (key.startsWith("msal.") ||
          key.includes("auth") ||
          key.includes("token") ||
          key.includes("account"))
      ) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));

    // Clear sessionStorage auth keys but preserve recovery tracking
    const recoveryData = {
      authRecoveryAttempts: sessionStorage.getItem("authRecoveryAttempts"),
      recoveryTimestamp: Date.now().toString(),
    };

    sessionStorage.clear();

    // Restore recovery tracking
    Object.entries(recoveryData).forEach(([key, value]) => {
      if (value) sessionStorage.setItem(key, value);
    });
  }

  if (redirectToHome) {
    const redirectUri = getEnvConfig().entraRedirect || window.location.origin;
    console.log("🔄 Redirecting to clean URL:", redirectUri);

    // Use replace to avoid adding to browser history
    window.location.replace(redirectUri);
  }
}

/**
 * Check if we recently performed a recovery
 */
export function wasRecentlyRecovered(): boolean {
  const recoveryTimestamp = sessionStorage.getItem("recoveryTimestamp");
  if (!recoveryTimestamp) {
    return false;
  }

  const recoveryTime = parseInt(recoveryTimestamp);
  const currentTime = Date.now();
  const fiveMinutesAgo = currentTime - 5 * 60 * 1000;

  return recoveryTime > fiveMinutesAgo;
}

/**
 * Clear recovery tracking (call when auth is working normally)
 */
export function clearRecoveryTracking(): void {
  sessionStorage.removeItem("authRecoveryAttempts");
  sessionStorage.removeItem("recoveryTimestamp");
  console.log("✅ Recovery tracking cleared - auth is working normally");
}

/**
 * Clear only MSAL localStorage entries (preserves other app data)
 * Useful for manual auth reset without full recovery
 */
export function clearMsalStorage(): void {
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith("msal.")) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach((key) => {
    localStorage.removeItem(key);
  });

  console.log(`🧹 Cleared ${keysToRemove.length} MSAL localStorage entries`);
}

/**
 * Initialize auth recovery monitoring
 */
export function initializeAuthRecoveryMonitoring(): void {
  // Track auth flow start
  trackAuthFlowStart();

  // Check if we're stuck in auth flow
  if (isInMicrosoftAuthFlow() && isAuthFlowStuck()) {
    console.warn("⚠️ Auth flow appears to be stuck, triggering recovery");
    performEmergencyAuthRecovery();
    return;
  }

  // Clear tracking if we're not in auth flow
  if (!isInMicrosoftAuthFlow()) {
    clearAuthFlowTracking();

    // Clear recovery tracking if we successfully navigated away
    if (wasRecentlyRecovered()) {
      clearRecoveryTracking();
    }
  }
}
